import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Activity, 
  Users, 
  TrendingUp, 
  Clock, 
  Filter, 
  Download,
  Trash2,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    action: 'all',
    entity_type: 'all',
    user_id: '',
    limit: 50
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.action && filters.action !== 'all') params.append('action', filters.action);
      if (filters.entity_type && filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      params.append('limit', filters.limit);

      const response = await axios.get(`${BACKEND_URL}/api/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/audit-logs/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchLogs();
  };

  const clearFilters = () => {
    setFilters({
      action: 'all',
      entity_type: 'all',
      user_id: '',
      limit: 50
    });
    setTimeout(() => fetchLogs(), 100);
  };

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity Name', 'Success'].join(','),
      ...logs.map(log => [
        log.timestamp,
        log.username,
        log.action,
        log.entity_type,
        log.entity_name || '',
        log.success
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString()}.csv`;
    a.click();
  };

  const getActionBadge = (action) => {
    const badges = {
      login: { color: 'bg-blue-500', icon: <Users className="w-3 h-3" />, label: 'Вход' },
      login_failed: { color: 'bg-red-500', icon: <XCircle className="w-3 h-3" />, label: 'Неудачный вход' },
      create_bus: { color: 'bg-purple-500', icon: <Plus className="w-3 h-3" />, label: 'Создание авто' },
      update_bus: { color: 'bg-yellow-500', icon: <Activity className="w-3 h-3" />, label: 'Обновление авто' },
      delete_bus: { color: 'bg-red-600', icon: <Trash2 className="w-3 h-3" />, label: 'Удаление авто' },
      create_refueling: { color: 'bg-cyan-500', icon: <Activity className="w-3 h-3" />, label: 'Заправка' },
      create_task: { color: 'bg-indigo-500', icon: <Plus className="w-3 h-3" />, label: 'Создание задачи' },
      create_repair: { color: 'bg-violet-500', icon: <Plus className="w-3 h-3" />, label: 'Создание ремонта' },
      approve_repair: { color: 'bg-green-500', icon: <CheckCircle className="w-3 h-3" />, label: 'Одобрение' },
      reject_repair: { color: 'bg-orange-500', icon: <XCircle className="w-3 h-3" />, label: 'Отклонение' },
    };

    const badge = badges[action] || { color: 'bg-gray-500', icon: <Activity className="w-3 h-3" />, label: action };
    return (
      <Badge className={`${badge.color} text-white flex items-center gap-1`}>
        {badge.icon}
        {badge.label}
      </Badge>
    );
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), 'dd MMM yyyy HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-6 space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            📊 Журнал аудита
          </h1>
          <p className="text-gray-600 mt-1">
            История всех действий в системе
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportLogs} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="export-logs-btn">
            <Download className="w-4 h-4 mr-2" />
            Экспорт
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500 shadow-lg hover:shadow-xl transition-shadow bg-white">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">Всего записей</CardTitle>
              <Activity className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{summary.total_logs}</div>
              <p className="text-xs text-gray-500 mt-1">
                в базе данных
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500 shadow-lg hover:shadow-xl transition-shadow bg-white">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">За 24 часа</CardTitle>
              <Clock className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{summary.recent_24h}</div>
              <p className="text-xs text-gray-500 mt-1">
                недавняя активность
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500 shadow-lg hover:shadow-xl transition-shadow bg-white">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">Активных пользователей</CardTitle>
              <Users className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{summary.top_users.length}</div>
              <p className="text-xs text-gray-500 mt-1">
                выполняли действия
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500 shadow-lg hover:shadow-xl transition-shadow bg-white">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">Типов действий</CardTitle>
              <TrendingUp className="h-5 w-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{summary.by_action.length}</div>
              <p className="text-xs text-gray-500 mt-1">
                различных операций
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="shadow-lg bg-white border-t-4 border-t-indigo-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-800">
            <Filter className="w-5 h-5 text-indigo-600" />
            Фильтры
          </CardTitle>
          <CardDescription className="text-gray-600">
            Отфильтруйте логи по типу действия, сущности или пользователю
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Фильтры в одну строку */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Действие</label>
                <Select value={filters.action} onValueChange={(value) => handleFilterChange('action', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Все действия" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все действия</SelectItem>
                    <SelectItem value="login">Вход</SelectItem>
                    <SelectItem value="login_failed">Неудачный вход</SelectItem>
                    <SelectItem value="create_bus">Создание автобуса</SelectItem>
                    <SelectItem value="update_bus">Обновление автобуса</SelectItem>
                    <SelectItem value="delete_bus">Удаление автобуса</SelectItem>
                    <SelectItem value="create_refueling">Добавление заправки</SelectItem>
                    <SelectItem value="create_task">Создание задачи</SelectItem>
                    <SelectItem value="create_repair">Создание ремонта</SelectItem>
                    <SelectItem value="approve_repair">Одобрение ремонта</SelectItem>
                    <SelectItem value="reject_repair">Отклонение ремонта</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Тип сущности</label>
                <Select value={filters.entity_type} onValueChange={(value) => handleFilterChange('entity_type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Все типы" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все типы</SelectItem>
                    <SelectItem value="auth">Аутентификация</SelectItem>
                    <SelectItem value="repair">Ремонт</SelectItem>
                    <SelectItem value="bus">Автобус</SelectItem>
                    <SelectItem value="task">Задача</SelectItem>
                    <SelectItem value="user">Пользователь</SelectItem>
                    <SelectItem value="refueling">Заправка</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Лимит записей</label>
                <Select value={filters.limit.toString()} onValueChange={(value) => handleFilterChange('limit', parseInt(value))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Кнопки на отдельной строке */}
            <div className="flex gap-3 justify-end pt-2">
              <Button onClick={clearFilters} variant="outline" className="hover:bg-gray-100 min-w-[120px]" data-testid="clear-filters-btn">
                Сбросить
              </Button>
              <Button onClick={applyFilters} className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-[140px]" data-testid="apply-filters-btn">
                <Search className="w-4 h-4 mr-2" />
                Применить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="shadow-lg bg-white">
        <CardHeader>
          <CardTitle className="text-gray-800">Логи событий ({logs.length})</CardTitle>
          <CardDescription className="text-gray-600">
            Последние события в системе
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Загрузка логов...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Логи не найдены</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Время</TableHead>
                    <TableHead>Пользователь</TableHead>
                    <TableHead>Действие</TableHead>
                    <TableHead>Тип</TableHead>
                    <TableHead>Название</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} data-testid={`audit-log-row-${log.id}`}>
                      <TableCell className="font-mono text-xs">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            <Users className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                          </div>
                          <span className="font-medium">{log.username}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getActionBadge(log.action)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.entity_type}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {log.entity_name || '-'}
                      </TableCell>
                      <TableCell>
                        {log.success ? (
                          <Badge className="bg-green-500 text-white">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Успешно
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500 text-white">
                            <XCircle className="w-3 h-3 mr-1" />
                            Ошибка
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedLog(log);
                            setShowDetails(true);
                          }}
                          data-testid={`view-details-btn-${log.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Детали
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Детали события</DialogTitle>
            <DialogDescription>
              Полная информация о событии в системе
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">ID события</label>
                  <p className="text-sm font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded mt-1">
                    {selectedLog.id}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Время</label>
                  <p className="text-sm bg-gray-100 dark:bg-gray-800 p-2 rounded mt-1">
                    {formatTimestamp(selectedLog.timestamp)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Пользователь</label>
                  <p className="text-sm bg-gray-100 dark:bg-gray-800 p-2 rounded mt-1">
                    {selectedLog.username}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">ID пользователя</label>
                  <p className="text-sm font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded mt-1">
                    {selectedLog.user_id}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Действие</label>
                  <div className="mt-1">
                    {getActionBadge(selectedLog.action)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Тип сущности</label>
                  <div className="mt-1">
                    <Badge variant="outline">{selectedLog.entity_type}</Badge>
                  </div>
                </div>
              </div>

              {selectedLog.entity_name && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Название сущности</label>
                  <p className="text-sm bg-gray-100 dark:bg-gray-800 p-2 rounded mt-1">
                    {selectedLog.entity_name}
                  </p>
                </div>
              )}

              {selectedLog.entity_id && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">ID сущности</label>
                  <p className="text-sm font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded mt-1">
                    {selectedLog.entity_id}
                  </p>
                </div>
              )}

              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Дополнительные детали</label>
                  <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded mt-1 overflow-auto max-h-60">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Статус выполнения</label>
                <div className="mt-1">
                  {selectedLog.success ? (
                    <Badge className="bg-green-500 text-white">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Успешно выполнено
                    </Badge>
                  ) : (
                    <div className="space-y-2">
                      <Badge className="bg-red-500 text-white">
                        <XCircle className="w-3 h-3 mr-1" />
                        Ошибка выполнения
                      </Badge>
                      {selectedLog.error_message && (
                        <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                          {selectedLog.error_message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogsPage;
